import { Link, useLocation } from 'react-router-dom'
import { Logo } from '@/components/brand/Logo'
import { Button } from './Button'
import { useState } from 'react'
import { cn } from '@/lib/utils'

const navLinks = [
  { label: 'Product', href: '/product' },
  { label: 'Integrations', href: '/integrations' },
  { label: 'Agencies', href: '/agencies' },
  { label: 'Pricing', href: '/pricing' },
]

export function SiteNav() {
  const [open, setOpen] = useState(false)
  const location = useLocation()

  return (
    <header className="sticky top-0 z-40 border-b border-border-gray bg-paper/95 backdrop-blur-sm">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
        <Link to="/" className="shrink-0">
          <Logo size="md" />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-6">
          {navLinks.map(link => (
            <Link
              key={link.href}
              to={link.href}
              className={cn(
                'text-sm font-medium transition-colors',
                location.pathname === link.href
                  ? 'text-human-teal'
                  : 'text-slate-600 hover:text-deep-ink'
              )}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <Link to="/signin">
            <Button variant="ghost" size="sm">Sign in</Button>
          </Link>
          <Link to="/signup">
            <Button variant="primary" size="sm">Get started</Button>
          </Link>
        </div>

        {/* Mobile hamburger */}
        <button
          className="md:hidden p-2 rounded-lg hover:bg-soft-panel"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          <div className="w-5 h-4 flex flex-col justify-between">
            <span className={cn('block h-0.5 bg-deep-ink transition-all', open && 'rotate-45 translate-y-1.5')} />
            <span className={cn('block h-0.5 bg-deep-ink transition-all', open && 'opacity-0')} />
            <span className={cn('block h-0.5 bg-deep-ink transition-all', open && '-rotate-45 -translate-y-1.5')} />
          </div>
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="md:hidden border-t border-border-gray bg-paper px-4 py-4 space-y-4">
          {navLinks.map(link => (
            <Link key={link.href} to={link.href} className="block text-sm font-medium text-slate-600" onClick={() => setOpen(false)}>
              {link.label}
            </Link>
          ))}
          <div className="pt-2 border-t border-border-gray flex flex-col gap-2">
            <Link to="/signin" onClick={() => setOpen(false)}>
              <Button variant="outline" size="md" className="w-full">Sign in</Button>
            </Link>
            <Link to="/signup" onClick={() => setOpen(false)}>
              <Button variant="primary" size="md" className="w-full">Get started</Button>
            </Link>
          </div>
        </div>
      )}
    </header>
  )
}

export function SiteFooter() {
  return (
    <footer className="border-t border-border-gray bg-paper">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12 grid grid-cols-2 md:grid-cols-4 gap-8">
        <div className="col-span-2 md:col-span-1">
          <Logo size="sm" />
          <p className="mt-3 text-xs text-slate-500 leading-relaxed">
            Turn past research into reusable echo panels.
          </p>
        </div>
        <div>
          <p className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-3">Product</p>
          <ul className="space-y-2 text-sm text-slate-600">
            <li><Link to="/product" className="hover:text-human-teal">Overview</Link></li>
            <li><Link to="/integrations" className="hover:text-human-teal">Integrations</Link></li>
            <li><Link to="/methodology" className="hover:text-human-teal">Methodology</Link></li>
            <li><Link to="/pricing" className="hover:text-human-teal">Pricing</Link></li>
            <li><Link to="/demo" className="hover:text-human-teal">Demo</Link></li>
          </ul>
        </div>
        <div>
          <p className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-3">Industries</p>
          <ul className="space-y-2 text-sm text-slate-600">
            <li><Link to="/industries/insurance" className="hover:text-human-teal">Insurance</Link></li>
            <li><Link to="/industries/financial-services" className="hover:text-human-teal">Financial Services</Link></li>
            <li><Link to="/industries/healthcare" className="hover:text-human-teal">Healthcare</Link></li>
            <li><Link to="/industries/saas" className="hover:text-human-teal">SaaS</Link></li>
          </ul>
        </div>
        <div>
          <p className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-3">Company</p>
          <ul className="space-y-2 text-sm text-slate-600">
            <li><Link to="/security" className="hover:text-human-teal">Security</Link></li>
            <li><Link to="/agencies" className="hover:text-human-teal">Agencies</Link></li>
            <li><Link to="/research-teams" className="hover:text-human-teal">Research Teams</Link></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-border-gray max-w-6xl mx-auto px-4 sm:px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-2">
        <p className="text-xs text-slate-400">© 2026 Echosome. Not affiliated with any survey platform or synthetic panel provider.</p>
        <p className="text-xs text-slate-400">Echosome does not replace real research.</p>
      </div>
    </footer>
  )
}
